/**
 * Lo que la institución configura por su cuenta.
 *
 * POR QUE EXISTE
 * ==============
 * Antes esta pantalla era un cascarón: cuatro tarjetas, tres deshabilitadas, y
 * la única viva llevaba a otra parte. Todo lo demás había que pedírselo al
 * proveedor, y una IPS que tiene que escribir a soporte para decidir quién
 * cancela una cita no está usando su producto: está esperando.
 *
 * EL REPARTO DE DECISIONES, VISIBLE
 * =================================
 * El proveedor decide QUÉ se vendió, la institución decide CÓMO opera. Eso no
 * se explica en un manual: se ve en la pantalla. Los módulos se muestran con
 * su estado y sin poder cambiarlos, y dicen a quién hay que pedírselo. Las
 * funcionalidades de dentro sí se tocan.
 *
 * CRITERIO
 * ========
 * Quien usa esto administra una clínica, no un servidor. Cada opción dice qué
 * hace y qué pasa si se apaga, con palabras de todos los días. Nada de códigos
 * ni de nombres técnicos.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Loader2, RotateCcw, Save, SlidersHorizontal } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import {
  getConfiguracion,
  guardarConfiguracion,
  type Decision,
  type FuncionalidadConfigurable,
  type Perfil,
} from '@/application/configuracion/configuracion.service'
import { toast } from '@/lib/toast'

/** Cómo se llama cada perfil para quien administra la clínica. */
const NOMBRE_DEL_PERFIL: Record<Perfil, string> = {
  ADMIN: 'Administrador',
  RADIOLOGIST: 'Radiólogo',
  TECHNICIAN: 'Tecnólogo',
  TRANSCRIPTOR: 'Transcriptor',
  DOCTOR: 'Médico remitente',
  PATIENT: 'Paciente',
}

/** Lo que el usuario ha cambiado y todavía no ha guardado. */
type Cambios = Record<string, { habilitada: boolean; perfiles: Perfil[] }>

const mismosPerfiles = (a: Perfil[], b: Perfil[]): boolean =>
  a.length === b.length && a.every((p) => b.includes(p))

export function ConfiguracionPage() {
  const queryClient = useQueryClient()
  const [cambios, setCambios] = useState<Cambios>({})

  const { data, isLoading, error } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => getConfiguracion(),
  })

  // Al recargar del servidor, lo pendiente deja de tener sentido.
  useEffect(() => {
    setCambios({})
  }, [data])

  const guardar = useMutation({
    mutationFn: (decisiones: Decision[]) => guardarConfiguracion(decisiones),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuracion'] })
      toast.success('Configuración guardada')
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'No se pudo guardar la configuración'),
  })

  const estadoDe = (f: FuncionalidadConfigurable) =>
    cambios[f.codigo] ?? { habilitada: f.habilitada, perfiles: f.perfiles }

  const cambiar = (f: FuncionalidadConfigurable, parcial: Partial<Cambios[string]>) => {
    const actual = estadoDe(f)
    setCambios((previo) => ({ ...previo, [f.codigo]: { ...actual, ...parcial } }))
  }

  const alternarPerfil = (f: FuncionalidadConfigurable, perfil: Perfil) => {
    const actual = estadoDe(f)
    const perfiles = actual.perfiles.includes(perfil)
      ? actual.perfiles.filter((p) => p !== perfil)
      : [...actual.perfiles, perfil]
    cambiar(f, { perfiles })
  }

  const decisiones = useMemo<Decision[]>(() => {
    if (!data) return []
    const todas = data.modulos.flatMap((m) => m.funcionalidades)
    return Object.entries(cambios).flatMap(([codigo, estado]) => {
      const original = todas.find((f) => f.codigo === codigo)
      if (!original) return []
      const igual =
        original.habilitada === estado.habilitada &&
        mismosPerfiles(original.perfiles, estado.perfiles)
      return igual ? [] : [{ codigo, habilitada: estado.habilitada, perfiles: estado.perfiles }]
    })
  }, [cambios, data])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
        <span className="text-[var(--foreground-muted)]">Cargando la configuración...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p>No pudimos cargar la configuración de su institución.</p>
          <p className="mt-2 text-sm text-[var(--foreground-muted)]">
            Inténtelo de nuevo o comuníquese con soporte.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <SlidersHorizontal className="h-6 w-6 text-[var(--primary)]" />
            Configuración
          </h1>
          <p className="mt-1 text-[var(--foreground-muted)]">
            Qué hace cada perfil dentro de lo que su institución tiene contratado.
          </p>
        </div>

        <Button
          onClick={() => guardar.mutate(decisiones)}
          disabled={decisiones.length === 0 || guardar.isPending}
        >
          {guardar.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {decisiones.length === 0
            ? 'Sin cambios'
            : `Guardar ${decisiones.length} cambio${decisiones.length === 1 ? '' : 's'}`}
        </Button>
      </div>

      {data.modulos.map((modulo) => (
        <Card key={modulo.codigo} className={modulo.habilitado ? '' : 'opacity-70'}>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle>{modulo.nombre}</CardTitle>
              {/* El módulo es alcance comercial: se ve, no se toca, y se dice
                  a quién hay que pedírselo. Sin esa frase, el administrador
                  cree que le falta un permiso. */}
              {/* Tres estados, no dos. "Fuera de su plan" y "contratado pero
                  apagado" se arreglan de formas distintas: uno cambiando de
                  plan y el otro pidiéndole al proveedor que lo encienda. */}
              <Badge variant={modulo.habilitado ? 'success' : 'default'}>
                <Lock className="mr-1 inline h-3 w-3" />
                {modulo.habilitado
                  ? 'Contratado'
                  : modulo.enElPlan
                    ? 'Incluido, sin activar'
                    : 'Fuera de su plan'}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-[var(--foreground-muted)]">{modulo.descripcion}</p>
            {!modulo.habilitado && (
              <p className="mt-2 text-sm">
                {modulo.enElPlan
                  ? 'Su plan lo incluye pero está sin activar. Escriba a soporte para encenderlo.'
                  : 'Su plan no lo incluye. Para tenerlo hay que cambiar de plan, no basta con activarlo.'}
              </p>
            )}
          </CardHeader>

          {modulo.habilitado && modulo.funcionalidades.length > 0 && (
            <CardContent className="space-y-5">
              {modulo.funcionalidades.map((f) => {
                const estado = estadoDe(f)
                const tocada = Boolean(cambios[f.codigo])

                return (
                  <div
                    key={f.codigo}
                    className={`rounded-lg border p-4 ${
                      tocada ? 'border-[var(--primary)]' : 'border-[var(--border)]'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">{f.nombre}</p>
                        <p className="mt-1 text-sm text-[var(--foreground-muted)]">
                          {f.descripcion}
                        </p>
                      </div>

                      <label className="flex flex-shrink-0 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={estado.habilitada}
                          onChange={(e) => cambiar(f, { habilitada: e.target.checked })}
                          aria-label={`Activar ${f.nombre}`}
                        />
                        <span className="text-sm">{estado.habilitada ? 'Activa' : 'Apagada'}</span>
                      </label>
                    </div>

                    {estado.habilitada && (
                      <div className="mt-4">
                        <p className="text-sm text-[var(--foreground-muted)]">
                          {f.delegable ? 'Quién puede hacerlo:' : 'Quién puede hacerlo (fijo):'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {data.perfiles.map((perfil) => {
                            const activo = estado.perfiles.includes(perfil)
                            return (
                              <button
                                key={perfil}
                                type="button"
                                disabled={!f.delegable}
                                onClick={() => alternarPerfil(f, perfil)}
                                aria-pressed={activo}
                                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                                  activo
                                    ? 'border-[var(--primary)] bg-[var(--primary)] text-white'
                                    : 'border-[var(--border)] text-[var(--foreground-muted)]'
                                } ${f.delegable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                              >
                                {NOMBRE_DEL_PERFIL[perfil]}
                              </button>
                            )
                          })}
                        </div>

                        {estado.perfiles.length === 0 && (
                          <p className="mt-2 text-sm text-[var(--danger)]">
                            Sin nadie seleccionado, esta opción no la podrá usar ninguna persona
                            de su institución.
                          </p>
                        )}

                        {!f.delegable && (
                          <p className="mt-2 text-sm text-[var(--foreground-muted)]">
                            Esta opción no se reparte: la decide el sistema.
                          </p>
                        )}
                      </div>
                    )}

                    {tocada && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() =>
                          setCambios((previo) => {
                            const copia = { ...previo }
                            delete copia[f.codigo]
                            return copia
                          })
                        }
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Deshacer
                      </Button>
                    )}
                  </div>
                )
              })}
            </CardContent>
          )}

          {modulo.habilitado && modulo.funcionalidades.length === 0 && (
            <CardContent>
              <p className="text-sm text-[var(--foreground-muted)]">
                Este módulo no tiene opciones que repartir: se tiene entero.
              </p>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  )
}
